import { useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { MoneyInput } from '@/components/money/MoneyInput';
import { EmptyState } from '@/components/ui/EmptyState';
import { MonthSwitcher } from '@/components/ui/MonthSwitcher';
import { currentYearMonth } from '@/lib/date';
import { formatCOP, formatDate } from '@/lib/format';
import { apiErrorMessage } from '@/lib/api';
import { useCategories } from '@/hooks/useCategories';
import { useCreateIncome, useDeleteIncome, useIncomes } from '@/hooks/useIncomes';
import { useCreateRecurringIncome, useRecurringIncomes, useUpdateRecurringIncome } from '@/hooks/useRecurring';

type RecurringFilter = 'ALL' | 'RECURRING' | 'VARIABLE';

export function IncomesPage() {
  const { data: categories } = useCategories('INCOME');
  const [month, setMonth] = useState(currentYearMonth);
  const [recurringFilter, setRecurringFilter] = useState<RecurringFilter>('ALL');
  const { data } = useIncomes({
    month,
    isRecurring: recurringFilter === 'ALL' ? undefined : recurringFilter === 'RECURRING',
  });
  const { data: recurringTemplates } = useRecurringIncomes();
  const createIncome = useCreateIncome();
  const createRecurringIncome = useCreateRecurringIncome();
  const updateRecurringIncome = useUpdateRecurringIncome();
  const deleteIncome = useDeleteIncome();

  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [receivedAt, setReceivedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [isRecurring, setIsRecurring] = useState(false);

  const activeTemplates = recurringTemplates?.filter((t) => t.active) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Ingresos</h1>
          <p className="text-sm text-muted-foreground">Lo que entra a tus cuentas este mes.</p>
        </div>
        <MonthSwitcher value={month} onChange={setMonth} />
      </div>

      <form
        className="card grid grid-cols-1 gap-3 sm:grid-cols-5 sm:items-end"
        onSubmit={async (e) => {
          e.preventDefault();
          if (amount <= 0 || !description) return;
          try {
            if (isRecurring) {
              await createRecurringIncome.mutateAsync({
                amount,
                description,
                categoryId: categoryId === '' ? null : categoryId,
                dayOfMonth: Number(receivedAt.slice(8, 10)),
                startMonth: receivedAt.slice(0, 7),
              });
              toast.success('Ingreso recurrente creado');
            } else {
              await createIncome.mutateAsync({
                amount,
                description,
                receivedAt,
                categoryId: categoryId === '' ? null : categoryId,
              });
              toast.success('Ingreso registrado');
            }
            setAmount(0);
            setDescription('');
            setIsRecurring(false);
          } catch (err) {
            toast.error(apiErrorMessage(err, 'No se pudo registrar el ingreso'));
          }
        }}
      >
        <div>
          <label className="text-xs font-medium text-muted-foreground">Monto</label>
          <MoneyInput value={amount} onChange={setAmount} />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Descripción</label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Salario" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Categoría</label>
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Sin categoría</option>
            {categories?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">
            {isRecurring ? 'Primer pago (día del mes)' : 'Fecha'}
          </label>
          <Input type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} />
        </div>
        <div className="flex items-center gap-2 sm:pb-2">
          <input
            id="isRecurring"
            type="checkbox"
            className="h-4 w-4 rounded border-border"
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
          />
          <label htmlFor="isRecurring" className="text-xs font-medium text-muted-foreground">
            Es recurrente (ej. salario)
          </label>
        </div>
        <Button type="submit" disabled={createIncome.isPending || createRecurringIncome.isPending}>
          Agregar
        </Button>
      </form>

      {activeTemplates.length > 0 && (
        <div className="card space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground">Recurrentes activos</h2>
          <div className="divide-y divide-border">
            {activeTemplates.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{t.description}</p>
                  <p className="text-xs text-muted-foreground">Día {t.dayOfMonth} de cada mes</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="font-medium text-income">{formatCOP(t.amount)}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={updateRecurringIncome.isPending}
                    onClick={async () => {
                      try {
                        await updateRecurringIncome.mutateAsync({ id: t.id, active: false });
                        toast.success('Ingreso recurrente desactivado');
                      } catch (err) {
                        toast.error(apiErrorMessage(err, 'No se pudo desactivar'));
                      }
                    }}
                  >
                    Desactivar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        {([
          ['ALL', 'Todos'],
          ['RECURRING', 'Recurrentes'],
          ['VARIABLE', 'Variables'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setRecurringFilter(value)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              recurringFilter === value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {data && data.items.length > 0 ? (
        <div className="card divide-y divide-border p-0">
          {data.items.map((income) => (
            <div key={income.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium">{income.description}</p>
                  {income.isRecurring && (
                    <Badge tone="income" className="shrink-0">
                      Recurrente
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{formatDate(income.receivedAt)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <span className="font-medium text-income">{formatCOP(income.amount)}</span>
                <button
                  type="button"
                  className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-destructive"
                  onClick={() => deleteIncome.mutate(income.id)}
                  aria-label="Eliminar"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState title="No hay ingresos este mes" description="Agrega tu primer ingreso con el formulario de arriba." />
      )}
    </div>
  );
}
